import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Button from '../../components/common/Button';
import { countriesApi, extractOne } from '../../api/facades';

const CountryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const numId = id ? parseInt(id, 10) : NaN;

  const { data: res, isLoading, isError } = useQuery({
    queryKey: ['countries', numId],
    queryFn: () => countriesApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const country = extractOne(res);

  if (Number.isNaN(numId) || isError || (!isLoading && !country)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-slate-600 dark:text-slate-400">Country not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/dashboard/settings/countries')}>
          Back to list
        </Button>
      </div>
    );
  }

  const states = (country && (country.states ?? (country as any).states)) || [];
  const statesList = Array.isArray(states) ? states : [];

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200">
          {isLoading ? 'Loading…' : country?.name ?? 'Country'}
        </h1>
        <Button variant="secondary" onClick={() => navigate('/dashboard/settings/countries')}>
          Back to list
        </Button>
      </div>
      {isLoading ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Code</dt>
            <dd className="text-slate-700 dark:text-slate-200">{country?.sortname ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wider">States</dt>
            <dd className="text-slate-700 dark:text-slate-200">{statesList.length}</dd>
          </div>
        </dl>
      )}
      {!isLoading && statesList.length > 0 && (
        <div className="mt-6">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">States</h2>
          <ul className="divide-y divide-gray-200 dark:divide-gray-600">
            {statesList.slice(0, 50).map((s: any) => (
              <li
                key={s.id}
                className="py-2 flex items-center justify-between"
              >
                <span className="text-slate-700 dark:text-slate-200">{s.name}</span>
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/settings/states/${s.id}`)}
                  className="text-primary-600 hover:text-primary-800 text-sm"
                >
                  View
                </button>
              </li>
            ))}
          </ul>
          {statesList.length > 50 && (
            <p className="text-sm text-slate-500 mt-2">Showing first 50 of {statesList.length} states.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default CountryDetailPage;
